import { withFormik } from 'formik';
import isEmail from 'isemail';
import moment from 'moment';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { createConfirmation } from 'react-confirm';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import { useBadRequestHandling } from '@shared/api/hooks/useBadRequestHandling';
import { CreateUserCertificateButton } from '@shared/certificates/components/CreateUserCertificateButton';
import { Portlet } from '@shared/ui/components/Portlet';

import Confirm from '../../base/Confirm';
import { COUNTRIES } from '../../constants/countries';
import { fromImmutable } from '../../libs/immutable-transformer';
import { baseValue, defaultSuccess, userHasPermission } from '../../libs/utils';
import { getCompanies } from '../../services/api/companies';
import { blacklistUser, impersonateUser, sendPasswordResetMail, unblacklistUser } from '../../services/api/users';
import '../../styles/vendor/react-checkbox-tree.css';
import BlacklistModal from '../admin/employees/partials/BlacklistModal';
import UserCertificates from './UserCertificates';
import BasicInput from './fields/BasicInput';
import BasicRadioList from './fields/BasicRadioList';
import BasicSelect from './fields/BasicSelect';
import CustomDatePicker from './fields/CustomDatePicker';

class InnerForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      defaultPermissions: this.props.userPermissions ? this.props.userPermissions : [],
      companies: [],
      userCompanies: props.user ? props.user.companies : [],
    };

    if (this.props.defaultCompany) {
      const comp = this.props.defaultCompany;
      this.state.userCompanies.push({
        ...comp,
        crewAdmin: false,
        gatekeepingAdmin: false,
        materialAdmin: false,
      });
    }

    this._handleCompanySearch = this._handleCompanySearch.bind(this);
    this._handleNewCompany = this._handleNewCompany.bind(this);
    this._handleCompanyDelete = this._handleCompanyDelete.bind(this);
    this._handleAdminChange = this._handleAdminChange.bind(this);
    this._passReset = this._passReset.bind(this);
    this._blacklist = this._blacklist.bind(this);
    this._unblacklist = this._unblacklist.bind(this);
    this.typeaheadRef = React.createRef();
  }

  static propTypes = {
    handleSubmit: PropTypes.func,
    formSubmitting: PropTypes.bool,
    setFieldValue: PropTypes.func,
    userPermissions: PropTypes.array,
    user: PropTypes.object,
    defaultCompany: PropTypes.object,
    company: PropTypes.bool,
  };

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    if (this.state.userCompanies !== nextState.userCompanies) {
      const value = nextState.userCompanies.map((company) => {
        return {
          id: company.id,
          gatekeepingAdmin: company.gatekeepingAdmin,
          crewAdmin: company.crewAdmin,
          materialAdmin: company.materialAdmin,
        };
      });

      this.props.setFieldValue('companies', value);
    }
  }

  _passReset() {
    Confirm('Are you sure you want to send a password reset?', {
      title: 'Send Password Reset',
      confirmStyle: 'btn-warning',
      confirmText: 'Send email',
    }).then(
      (result) => {
        sendPasswordResetMail(this.props.user.id).then((response) => {
          defaultSuccess('Password reset sent!');
        });
      },
      (result) => {}
    );
  }

  _handleCompanySearch(value) {
    const currentCompanies = this.state.userCompanies.map((company) => company.id);
    getCompanies({
      'filter[name]': value,
    }).then((response) => {
      this.setState({
        companies: response.data.filter((company) => currentCompanies.indexOf(company.id) === -1),
      });
    });
  }

  _handleNewCompany(value) {
    if (value.length > 0) {
      let company = value[0];
      company.gatekeepingAdmin = false;
      company.materialAdmin = false;
      company.crewAdmin = false;

      let userCompanies = this.state.userCompanies.concat(company);
      const currentCompanies = userCompanies.map((company) => company.id);

      this.setState({
        userCompanies,
        companies: this.state.companies.filter((company) => currentCompanies.indexOf(company.id) === -1),
      });

      this.typeaheadRef.current.clear();
    }
  }

  _handleCompanyDelete(id) {
    Confirm('Are you sure you want to delete this Company? This action can not be undone.', {
      title: 'Delete Company',
      confirmStyle: 'btn-danger',
      confirmText: 'Delete',
    }).then(
      (result) => {
        this.setState({
          userCompanies: this.state.userCompanies.filter((company) => company.id !== id),
        });
      },
      (result) => {}
    );
  }

  _handleAdminChange(e, id, key) {
    let userCompanies = this.state.userCompanies.map((company) => {
      let newComp = fromImmutable(company);
      if (newComp.id === id) {
        newComp[key] = e.target.checked;
        return newComp;
      } else {
        return newComp;
      }
    });

    this.setState({ userCompanies });
  }

  _blacklist() {
    const confirm = createConfirmation(BlacklistModal);
    confirm({ mode: 'blacklist' }).then(
      ({ values }) => {
        blacklistUser(this.props.user.id, values).then((response) => {
          defaultSuccess('User Blacklisted');
          if (this.props.reloadUser) {
            this.props.reloadUser(); // Reload user data
          }
        });
      },
      (result) => {}
    );
  }

  _unblacklist() {
    const confirm = createConfirmation(BlacklistModal);
    confirm({ options: { mode: 'unblacklist' } }).then(
      ({ values }) => {
        unblacklistUser(this.props.user.id, values).then((response) => {
          defaultSuccess('User UnBlacklisted');
          if (this.props.reloadUser) {
            this.props.reloadUser(); // Reload user data
          }
        });
      },
      (result) => {}
    );
  }

  render() {
    const props = this.props;
    const { company, user, authUser } = this.props;
    const queryParams = queryString.parse(window.location.search);

    return (
      <>
        <div className="row">
          {user && user.blacklisted && (
            <div className="alert alert-danger">
              {!user.blacklistReason && <Trans>This user has been Blacklisted</Trans>}
              {user.blacklistReason && (
                <>
                  <h4 className="alert-heading">
                    <Trans>This user has been Blacklisted</Trans>
                  </h4>
                  <p>
                    Reason: <em>{user.blacklistReason}</em>
                  </p>
                  <hr />
                  <p className="mb-0">
                    Source: <strong>{user.blacklistSource}</strong>,{' '}
                    {user.blacklistedByUser ? (
                      <>
                        blacklisted by{' '}
                        <strong>
                          {user.blacklistedByUser.firstName} {user.blacklistedByUser.lastName}
                        </strong>
                      </>
                    ) : (
                      ''
                    )}{' '}
                    on <strong>{user.blacklistedAt}</strong>
                  </p>
                </>
              )}
            </div>
          )}
        </div>
        <form onSubmit={this.props.handleSubmit}>
          <div className="row">
            <div className={company ? 'col-xl-12' : 'col-xl-6'}>
              <div className="m-portlet">
                <div className="m-portlet__head">
                  <div className="m-portlet__head-caption">
                    <div className="m-portlet__head-title">
                      <span className="m-portlet__head-icon">
                        <i className="flaticon-information" />
                      </span>
                      <h3 className="m-portlet__head-text">
                        <Trans>Employee Info</Trans>
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="m-portlet__body">
                  <BasicInput {...props} name="firstName" type="text" label="First Name" />

                  <BasicInput {...props} name="lastName" type="text" label="Last Name" />

                  <BasicInput {...props} name="email" type="email" label="E-mail" />

                  <BasicSelect
                    {...this.props}
                    name="nationality"
                    label="Nationality"
                    placeholder="Select Nationality"
                    disabled={!!queryParams.nationality}
                    options={COUNTRIES.map((country) => {
                      return { value: country.value, title: country.label };
                    })}
                  />

                  <BasicInput
                    {...props}
                    name="nationalRegistryNumber"
                    type="text"
                    label="National Registry Number"
                    disabled={!!queryParams.nationalRegistryNumber}
                  />

                  <ReactTooltip />

                  <BasicRadioList
                    {...props}
                    name="vaccinated"
                    label="This crew member complies with every regulation necessary to work on the next event."
                    extra={
                      <a
                        href="http://tomorrowland.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="m-badge m-badge--brand m-badge--wide"
                        data-tip="*Please check the most recent crew guidelines of the upcoming event, for more information."
                        data-multiline={true}
                      >
                        More Info
                      </a>
                    }
                    options={
                      props.user
                        ? [
                            { value: 'nochange', label: "Don't Change" },
                            { value: 'Yes', label: 'Yes' },
                            { value: 'No', label: 'No' },
                          ]
                        : [
                            { value: 'Yes', label: 'Yes' },
                            { value: 'No', label: 'No' },
                          ]
                    }
                  />

                  <BasicInput {...props} name="telephone" type="text" label="Phone" />

                  <BasicSelect
                    {...props}
                    name="size"
                    label="Size"
                    placeholder="Select T-shirt Size"
                    options={[
                      { value: 'XS', title: 'Extra Small' },
                      { value: 'S', title: 'Small' },
                      { value: 'M', title: 'Medium' },
                      { value: 'L', title: 'Large' },
                      { value: 'XL', title: 'XL' },
                      { value: 'XXL', title: 'XXL' },
                    ]}
                  />

                  <BasicRadioList
                    {...props}
                    name="gender"
                    label="Gender"
                    options={[
                      { value: 'female', label: 'Female' },
                      { value: 'male', label: 'Male' },
                      { value: 'other', label: 'Other' },
                    ]}
                  />

                  <CustomDatePicker
                    {...props}
                    name="dateofbirth"
                    label={'Date of Birth'}
                    showMonthDropdown
                    showYearDropdown
                  />
                </div>
                {company && (
                  <div className="m-portlet__foot">
                    <div className="row align-items-center">
                      <div className="col-lg-12 m--align-right">
                        <button
                          className={
                            'btn m-btn btn-secondary m--margin-left-10' +
                            (props.formSubmitting ? ' m-btn--custom m-loader m-loader--light m-loader--left' : '')
                          }
                          disabled={props.formSubmitting}
                        >
                          {user ? <Trans>Update</Trans> : <Trans>Create</Trans>}
                        </button>
                        <span className="m--margin-left-10">
                          <Trans>or</Trans>{' '}
                          <Link to="/company/employees" className="m-link m--font-bold">
                            <Trans>Cancel</Trans>
                          </Link>
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {!company && (
              <div className="col-xl-6">
                <div className="m-portlet">
                  <div className="m-portlet__head">
                    <div className="m-portlet__head-caption">
                      <div className="m-portlet__head-title">
                        <span className="m-portlet__head-icon">
                          <i className="flaticon-lock-1" />
                        </span>
                        <h3 className="m-portlet__head-text">
                          <Trans>Companies</Trans>
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div className="m-portlet__body">
                    <div className="form-group m-form__group">
                      <AsyncTypeahead
                        multiple={false}
                        id={'companyselector'}
                        options={this.state.companies}
                        isLoading={false}
                        placeholder={'Add a company...'}
                        minLength={2}
                        labelKey="name"
                        onSearch={this._handleCompanySearch}
                        ref={this.typeaheadRef}
                        onChange={this._handleNewCompany}
                      />
                    </div>

                    {this.state.userCompanies.length > 0 && (
                      <div className="m-widget1 m-widget1--paddingless">
                        {this.state.userCompanies.map((company) => (
                          <div className="m-widget1__item" key={company.id}>
                            <h3 className="m-widget1__title m--margin-bottom-10">
                              {company.name}
                              <button
                                className="btn btn-outline-danger m-btn btn-sm  m-btn--icon m--margin-left-5 float-right"
                                onClick={() => this._handleCompanyDelete(company.id)}
                                type="button"
                              >
                                <span>
                                  <i className="la la-trash" />
                                  <span>
                                    <Trans>Delete</Trans>
                                  </span>
                                </span>
                              </button>
                            </h3>
                            <div className="m-widget1__desc">
                              <div className="m-checkbox-list">
                                <label className="m-checkbox">
                                  <input
                                    type="checkbox"
                                    defaultChecked={company.crewAdmin === 1}
                                    onClick={(e) => this._handleAdminChange(e, company.id, 'crewAdmin')}
                                  />{' '}
                                  <Trans>Crew Admin</Trans>
                                  <span />
                                </label>
                                <label className="m-checkbox">
                                  <input
                                    type="checkbox"
                                    defaultChecked={company.gatekeepingAdmin === 1}
                                    onClick={(e) => this._handleAdminChange(e, company.id, 'gatekeepingAdmin')}
                                  />{' '}
                                  <Trans>Gatekeeping Admin</Trans>
                                  <span />
                                </label>
                                <label className="m-checkbox">
                                  <input
                                    type="checkbox"
                                    defaultChecked={company.materialAdmin === 1}
                                    onClick={(e) => this._handleAdminChange(e, company.id, 'materialAdmin')}
                                  />{' '}
                                  <Trans>Material Admin</Trans>
                                  <span />
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="m-portlet__foot">
                    <div className="row align-items-center">
                      {props.user && (
                        <div className="col-lg-8 flex gap-2 items-center">
                          <button
                            className={
                              'btn m-btn btn-primary' +
                              (props.formSubmitting ? ' m-btn--custom m-loader m-loader--light m-loader--left' : '')
                            }
                            disabled={props.formSubmitting}
                          >
                            <Trans>Update</Trans>
                          </button>

                          <span>
                            <Trans>or</Trans>{' '}
                            <Link to="/admin/companies" className="m-link m--font-bold">
                              <Trans>Cancel</Trans>
                            </Link>
                          </span>
                        </div>
                      )}

                      {!props.user && (
                        <div className="col-lg-12 m--align-right">
                          <button
                            className={
                              'btn m-btn btn-secondary m--margin-left-10' +
                              (props.formSubmitting ? ' m-btn--custom m-loader m-loader--light m-loader--left' : '')
                            }
                            disabled={props.formSubmitting}
                          >
                            <Trans>Create</Trans>
                          </button>
                          <span className="m--margin-left-10">
                            <Trans>or</Trans>{' '}
                            <Link to="/admin/companies" className="m-link m--font-bold">
                              <Trans>Cancel</Trans>
                            </Link>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {props.user && (
                  <Portlet title="User Actions" icon="gear">
                    <div className="flex gap-2 items-center">
                      <button className="btn m-btn btn-outline-info" type="button" onClick={this._passReset}>
                        <Trans>Password Reset</Trans>
                      </button>

                      <ImpersonateButton user={user} />

                      {userHasPermission(authUser, ['users.blacklist']) && user && !user.blacklisted && (
                        <button className="btn btn-danger" onClick={this._blacklist} type="button">
                          <Trans>Blacklist</Trans>
                        </button>
                      )}

                      {userHasPermission(authUser, ['users.blacklist']) && user && user.blacklisted && (
                        <button className="btn btn-danger" onClick={this._unblacklist} type="button">
                          <Trans>Forgive Sins</Trans>
                        </button>
                      )}
                    </div>
                  </Portlet>
                )}
              </div>
            )}
          </div>
        </form>
        {(user !== undefined) && <div className="row">
          <div className="col-md-6">
            <Portlet
              title="Certificates"
              actions={<CreateUserCertificateButton userId={user.id} />}
              icon={'certificate'}
            >
              <UserCertificates user={this.props.user} />
            </Portlet>
          </div>
        </div>}
      </>
    );
  }
}

const CreateUserForm = withFormik({
  mapPropsToValues: (props) => {
    const employee = baseValue(props.user, {});

    const queryParams = queryString.parse(window.location.search);

    let values = {
      firstName: baseValue(employee.firstName),
      lastName: baseValue(employee.lastName),
      email: baseValue(employee.email),
      nationality: queryParams.nationality ? queryParams.nationality : baseValue(employee.nationality),
      nationalRegistryNumber: queryParams.nationalRegistryNumber
        ? queryParams.nationalRegistryNumber
        : employee.nationalRegistryNumberLastDigits
          ? 'XXXXXXXX' + employee.nationalRegistryNumberLastDigits
          : undefined,
      telephone: baseValue(employee.telephone),
      size: baseValue(employee.size),
      gender: baseValue(employee.gender),
      dateofbirth: employee.birthDate ? moment(employee.birthDate) : undefined,
      companies: baseValue(employee.companies, []),
      vaccinated: undefined,
    };

    if (props.user) {
      values.vaccinated = 'nochange';
    }

    if (props.defaultCompany) {
      const comp = props.defaultCompany;
      values.companies.push({
        id: comp.id,
        crew_admin: false,
        gatekeeping_admin: false,
        material_admin: false,
      });
    }

    return values;
  },
  validate: (values, props) => {
    if (props.overwriteValidation) {
      return props.overwriteValidation(values);
    } else {
      let errors = {};

      if (!values.firstName || values.firstName === '') {
        errors.firstName = 'required';
      }

      if (values.email && !isEmail.validate(values.email)) {
        errors.email = 'Invalid email address';
      }

      let admin = false;

      values.companies.forEach((company) => {
        if (company.crew_admin || company.gatekeeping_admin || company.material_admin) {
          admin = true;
        }
      });

      if (admin && (!values.email || values.email === '')) {
        errors.email = 'Admins need an email';
      }

      return errors;
    }
  },

  handleSubmit: (values, { props, setSubmitting }) => {
    return props.onSubmit(values);
  },
})(InnerForm);

export default CreateUserForm;

function ImpersonateButton({ user }) {
  const { withBadRequestHandling } = useBadRequestHandling();

  async function impersonate() {
    await withBadRequestHandling(async () => {
      await impersonateUser(user.id);
    });
  }

  return (
    <button className="btn m-btn btn-outline-warning" type="button" onClick={impersonate}>
      <Trans>Impersonate</Trans>
    </button>
  );
}
